import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { createShipEngineLabel, getShipEngineLabel, quoteShipEngineShipment } from '../services/shipEngineService';

const createPackage = (id) => ({
  id,
  weightLb: '1',
  lengthIn: '20',
  widthIn: '15',
  heightIn: '15'
});

const ADDRESS_CITY_STATE_ZIP_REGEX = /^(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;

const parseAddressBlock = (rawText) => {
  const lines = String(rawText || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 3) return null;

  const name = lines[0] || '';
  const street1 = lines[1] || '';
  const cityStateZipLine = lines[2] || '';
  const match = cityStateZipLine.match(ADDRESS_CITY_STATE_ZIP_REGEX);
  if (!match) return null;

  const [, city, state, postalCode] = match;
  const country = lines[3] && lines[3].length <= 3 ? lines[3].toUpperCase() : 'US';

  return {
    name,
    street1,
    city: city.trim(),
    state: state.toUpperCase(),
    postalCode,
    country
  };
};

const ShippingModal = ({
  isOpen,
  onClose,
  onShipmentPurchased,
  onShippingDataChange,
  shippingContext = {},
  initialShippingData = null
}) => {
  const [recipient, setRecipient] = useState({
    name: '',
    email: '',
    phone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });
  const [packages, setPackages] = useState([createPackage(1)]);
  const [loadingRate, setLoadingRate] = useState(false);
  const [loadingPurchase, setLoadingPurchase] = useState(false);
  const [loadingLabelRetrieve, setLoadingLabelRetrieve] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [addressPasteInput, setAddressPasteInput] = useState('');

  const getFirebaseErrorMessage = (err) => {
    if (!err) return 'Unknown error';
    return (
      err?.details ||
      err?.customData?.details ||
      err?.message ||
      'Unknown error'
    );
  };

  useEffect(() => {
    if (!isOpen) return;

    setError('');
    setQuote(null);
    setPurchaseResult(null);
    setAddressPasteInput('');

    if (initialShippingData?.totalShipmentCost != null) {
      setQuote({
        totalShipmentCost: initialShippingData.totalShipmentCost,
        preferredRate: initialShippingData.preferredRate || null
      });
    }

    if (initialShippingData?.trackingNumber || initialShippingData?.labelDownloadUrl || initialShippingData?.labelId) {
      setPurchaseResult({
        totalShipmentCost: initialShippingData.totalShipmentCost,
        trackingNumber: initialShippingData.trackingNumber,
        labelId: initialShippingData.labelId || null,
        labelDownloadUrl: initialShippingData.labelDownloadUrl || null,
        labels: initialShippingData.labels || null
      });
    }

    if (initialShippingData?.recipient) {
      setRecipient({
        name: initialShippingData.recipient.name || '',
        email: initialShippingData.recipient.email || '',
        phone: initialShippingData.recipient.phone || '',
        street1: initialShippingData.recipient.street1 || '',
        street2: initialShippingData.recipient.street2 || '',
        city: initialShippingData.recipient.city || '',
        state: initialShippingData.recipient.state || '',
        postalCode: initialShippingData.recipient.postalCode || '',
        country: initialShippingData.recipient.country || 'US'
      });
    } else {
      setRecipient({
        name: shippingContext.customerName || '',
        email: shippingContext.customerEmail || '',
        phone: shippingContext.customerPhone || '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      });
    }

    if (Array.isArray(initialShippingData?.packages) && initialShippingData.packages.length > 0) {
      setPackages(
        initialShippingData.packages.map((pkg, index) => ({
          id: index + 1,
          weightLb: String(pkg.weightLb || ((Number(pkg.weightOz) || 16) / 16)),
          lengthIn: String(pkg.lengthIn || 20),
          widthIn: String(pkg.widthIn || 15),
          heightIn: String(pkg.heightIn || 15)
        }))
      );
      return;
    }

    setPackages([createPackage(1)]);
  }, [isOpen, initialShippingData, shippingContext]);

  const canQuote = useMemo(() => {
    return Boolean(
      recipient.name &&
      recipient.street1 &&
      recipient.city &&
      recipient.state &&
      recipient.postalCode &&
      recipient.country &&
      packages.length > 0 &&
      packages.every(
        (pkg) =>
          Number(pkg.weightLb) > 0 &&
          Number(pkg.lengthIn) > 0 &&
          Number(pkg.widthIn) > 0 &&
          Number(pkg.heightIn) > 0
      )
    );
  }, [recipient, packages]);

  const handlePackageChange = (id, field, value) => {
    setPackages((prev) => prev.map((pkg) => (pkg.id === id ? { ...pkg, [field]: value } : pkg)));
  };

  const handleAddPackage = () => {
    setPackages((prev) => [...prev, createPackage(Math.max(0, ...prev.map((pkg) => pkg.id)) + 1)]);
  };

  const handleRemovePackage = (id) => {
    setPackages((prev) => prev.filter((pkg) => pkg.id !== id));
  };

  const handleParseAddress = () => {
    const parsed = parseAddressBlock(addressPasteInput);
    if (!parsed) {
      setError('Could not parse address. Use format: Name, Street, City ST ZIP');
      return;
    }

    setError('');
    setRecipient((prev) => ({
      ...prev,
      name: parsed.name || prev.name,
      street1: parsed.street1 || prev.street1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      postalCode: parsed.postalCode || prev.postalCode,
      country: parsed.country || prev.country
    }));
  };

  const serializePackages = () => packages.map((pkg) => ({
    weightLb: Number(pkg.weightLb) || 0,
    lengthIn: Number(pkg.lengthIn) || 0,
    widthIn: Number(pkg.widthIn) || 0,
    heightIn: Number(pkg.heightIn) || 0
  }));

  const buildShippingSnapshot = () => {
    const snapshot = {
      recipient: { ...recipient },
      packages: serializePackages()
    };

    if (quote?.totalShipmentCost != null) {
      snapshot.totalShipmentCost = quote.totalShipmentCost;
      snapshot.preferredRate = quote.preferredRate || null;
    }

    if (purchaseResult) {
      snapshot.trackingNumber = purchaseResult.trackingNumber || '';
      snapshot.labelId = purchaseResult.labelId || null;
      snapshot.labelDownloadUrl = purchaseResult.labelDownloadUrl || null;
      snapshot.labels = purchaseResult.labels || null;
      if (purchaseResult.totalShipmentCost != null) {
        snapshot.totalShipmentCost = purchaseResult.totalShipmentCost;
      }
    }

    return snapshot;
  };

  const persistShippingDraft = () => {
    onShippingDataChange?.(buildShippingSnapshot());
  };

  const handleClose = () => {
    persistShippingDraft();
    onClose?.();
  };

  const buildPayload = () => ({
    diagnostics: true,
    rateId: quote?.preferredRate?.rateId || null,
    carrierCode: quote?.preferredRate?.carrierCode || 'ups',
    serviceCode: quote?.preferredRate?.serviceCode || 'ups_ground',
    packageCode: quote?.preferredRate?.packageCode || 'package',
    confirmation: 'delivery',
    recipient,
    packages: serializePackages(),
    orderReference:
      shippingContext.orderReference ||
      `WEB-${Date.now()}`,
    customerReference: shippingContext.customerReference || ''
  });

  const handleGetRate = async () => {
    if (!canQuote || loadingRate) return;
    setError('');
    setLoadingRate(true);

    try {
      const result = await quoteShipEngineShipment(buildPayload());
      if (!result?.success) {
        throw new Error(result?.error || 'Unable to get shipping rate');
      }
      setQuote(result);
    } catch (err) {
      setQuote(null);
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoadingRate(false);
    }
  };

  const handleRetrieveLabel = async () => {
    const labelId = purchaseResult?.labelId || initialShippingData?.labelId;
    if (!labelId || loadingLabelRetrieve) return;

    setError('');
    setLoadingLabelRetrieve(true);
    try {
      const result = await getShipEngineLabel(labelId);
      if (!result?.success || !result?.labelDownloadUrl) {
        throw new Error(result?.error || 'Unable to retrieve label download link');
      }
      const nextPurchaseResult = {
        ...(purchaseResult || {}),
        ...result
      };
      setPurchaseResult(nextPurchaseResult);
      onShippingDataChange?.({
        ...buildShippingSnapshot(),
        ...nextPurchaseResult
      });
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoadingLabelRetrieve(false);
    }
  };

  const handlePurchaseLabel = async () => {
    if (!canQuote || loadingPurchase) return;
    setError('');
    setLoadingPurchase(true);
    try {
      const result = await createShipEngineLabel(buildPayload());
      if (!result?.success) {
        throw new Error(result?.error || 'Unable to create shipping label');
      }
      setPurchaseResult(result);
      const snapshot = {
        ...result,
        preferredRate: quote?.preferredRate || result?.preferredRate || null,
        recipient: { ...recipient },
        packages: serializePackages()
      };
      onShippingDataChange?.(snapshot);
      onShipmentPurchased?.(snapshot);
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoadingPurchase(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: 'var(--al-bg)',
          color: 'white',
          borderRadius: 0
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
          Create Shipping Label (UPS Ground)
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
            Quick Paste Address
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Paste address block"
            value={addressPasteInput}
            onChange={(e) => setAddressPasteInput(e.target.value)}
            placeholder={'Ben Anderson\n505 Prarie View Drive\nWest Des Moines, IA 50266'}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={handleParseAddress} disabled={!addressPasteInput.trim()}>
              Parse Address
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
              Recipient Address
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Recipient Name" fullWidth value={recipient.name} onChange={(e) => setRecipient((prev) => ({ ...prev, name: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" fullWidth value={recipient.email} onChange={(e) => setRecipient((prev) => ({ ...prev, email: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone" fullWidth value={recipient.phone} onChange={(e) => setRecipient((prev) => ({ ...prev, phone: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Street 1" fullWidth value={recipient.street1} onChange={(e) => setRecipient((prev) => ({ ...prev, street1: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Street 2" fullWidth value={recipient.street2} onChange={(e) => setRecipient((prev) => ({ ...prev, street2: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="City" fullWidth value={recipient.city} onChange={(e) => setRecipient((prev) => ({ ...prev, city: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="State" fullWidth value={recipient.state} onChange={(e) => setRecipient((prev) => ({ ...prev, state: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Postal Code" fullWidth value={recipient.postalCode} onChange={(e) => setRecipient((prev) => ({ ...prev, postalCode: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Country" fullWidth value={recipient.country} onChange={(e) => setRecipient((prev) => ({ ...prev, country: e.target.value }))} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.85)' }}>
            Packages (Default: one box)
          </Typography>
          {packages.map((pkg) => (
            <Box
              key={pkg.id}
              sx={{
                border: '1px solid rgba(255,255,255,0.12)',
                p: 2,
                mb: 1,
                backgroundColor: 'rgba(255,255,255,0.02)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Box #{pkg.id}</Typography>
                {packages.length > 1 && (
                  <IconButton sx={{ color: '#ff6666' }} size="small" onClick={() => handleRemovePackage(pkg.id)}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="number"
                    label="Weight (lb)"
                    fullWidth
                    value={pkg.weightLb}
                    onChange={(e) => handlePackageChange(pkg.id, 'weightLb', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="number"
                    label="Length (in)"
                    fullWidth
                    value={pkg.lengthIn}
                    onChange={(e) => handlePackageChange(pkg.id, 'lengthIn', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="number"
                    label="Width (in)"
                    fullWidth
                    value={pkg.widthIn}
                    onChange={(e) => handlePackageChange(pkg.id, 'widthIn', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="number"
                    label="Height (in)"
                    fullWidth
                    value={pkg.heightIn}
                    onChange={(e) => handlePackageChange(pkg.id, 'heightIn', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddPackage}>
            Add Box
          </Button>
        </Box>

        {quote?.totalShipmentCost != null && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(var(--al-primary-rgb), 0.3)', backgroundColor: 'rgba(var(--al-primary-rgb), 0.08)' }}>
            <Typography sx={{ fontWeight: 600 }}>
              Quoted Shipping: ${Number(quote.totalShipmentCost).toFixed(2)}
            </Typography>
          </Box>
        )}

        {purchaseResult?.totalShipmentCost != null && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(var(--al-primary-rgb), 0.4)', backgroundColor: 'rgba(var(--al-primary-rgb), 0.12)' }}>
            <Typography sx={{ fontWeight: 600 }}>
              Label Purchased: ${Number(purchaseResult.totalShipmentCost).toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Tracking: {purchaseResult.trackingNumber || 'n/a'}
            </Typography>
            {purchaseResult.labelDownloadUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => window.open(purchaseResult.labelDownloadUrl, '_blank', 'noopener,noreferrer')}
                >
                  Download Label (PDF)
                </Button>
              </Box>
            )}
            {!purchaseResult.labelDownloadUrl && purchaseResult.labelId && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant="outlined"
                  onClick={handleRetrieveLabel}
                  disabled={loadingLabelRetrieve}
                >
                  {loadingLabelRetrieve ? 'Retrieving...' : 'Retrieve Label Download'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Box sx={{ mt: 2, p: 1.5, border: '1px solid rgba(255,99,99,0.5)', backgroundColor: 'rgba(255,99,99,0.12)' }}>
            <Typography color="#ff9a9a" variant="body2">
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose}>Close</Button>
        <Button onClick={handleGetRate} disabled={!canQuote || loadingRate}>
          {loadingRate ? 'Calculating...' : 'Calculate Shipping'}
        </Button>
        <Button variant="contained" onClick={handlePurchaseLabel} disabled={!canQuote || loadingPurchase || purchaseResult?.labelDownloadUrl}>
          {loadingPurchase ? 'Purchasing...' : purchaseResult?.labelDownloadUrl ? 'Label Purchased' : 'Create & Pay Label'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShippingModal;
